import { createFileRoute, Outlet } from '@tanstack/react-router'
import { noop } from '@tanstack/react-query'
import { trpc } from '@/main'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import { useSessionUser } from '@/hooks/use-session'
import { useLingui } from '@lingui/react/macro'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { SettingsLayoutPending } from './-components/settings/account-pending'
import { SettingsShell } from './-components/settings/settings-shell'
import { SettingsSidebar } from './-components/settings-sidebar'
import { AccountBreadcrumbs } from './-components/account-breadcrumbs'

type SessionUser = inferRouterOutputs<AppRouter>['auth']['getUser']

export const Route = createFileRoute('/_app/account')({
  // The Settings sidebar and breadcrumbs list every integration; a professional's own profile page draws neither.
  context: () => ({ appsQueryOptions: trpc.apps.getAll.queryOptions() }),
  loader: ({ context }) => {
    // The session the root warmed, when already in hand; a document's first loader reads the list on render instead.
    const user = context.queryClient.getQueryData(context.sessionUserQueryOptions.queryKey)
    if (user !== undefined && !showsProfileData(user)) context.queryClient.query(context.appsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: SettingsLayoutPending,
})

/**
 * A professional in their OWN profile reads "My profile data"; a company account OR an admin/authorized employee
 * acting AS the company (company view → /account shows the company profile) reads "Settings" (Configuración).
 */
function showsProfileData(user: SessionUser) {
  return user?.type === 'applicant' && !(user.isEmployee && user.interfaceMode === 'company')
}

function RouteComponent() {
  const { t } = useLingui()
  const user = useSessionUser()
  // Any professional (self-registered or a company-registered employee) sees their own profile data, not the
  // company Settings (no Billing/Integrations — those stay owner-only), so no sidebar either: one page. Keep the gate
  // a pure applicant check so a company-view admin still doesn't get Billing/Integrations.
  const isApplicant = user?.type === 'applicant'
  const showProfileData = showsProfileData(user)
  return (
    <SettingsShell
      icon={showProfileData ? NAVIGATION_ICONS.applicant : NAVIGATION_ICONS.settings}
      title={showProfileData ? t`My profile data` : t`Account`}
      breadcrumbs={showProfileData ? undefined : <AccountBreadcrumbs />}
      sidebar={isApplicant ? undefined : (className) => <SettingsSidebar className={className} />}
      contentTestId="account-content"
    >
      <Outlet />
    </SettingsShell>
  )
}
